import { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "react-toastify";
import { M_URL } from "../../constants";
import axios from "axios";

/**
 * ProgressIndicator
 * ---------------------------------------------------------------
 * WebSocket'dan keladigan progress xabarlarini tinglaydi va FAQAT
 * eng so'nggi holatni ko'rsatadi (eskisi yangisi bilan almashtiriladi).
 *
 * Kutilayotgan xabar formati (JSON):
 * {
 *   "event": "progress",
 *   "task_id": "48c88af5-020d-42b9-b982-19ce836dff64",
 *   "current_page": 3
 * }
 * yoki
 * {
 *   "event": "completed",
 *   "task_id": "..."
 * }
 *
 * "completed" kelganda /check-video sahifasiga avtomatik o'tadi.
 *
 * Ishlatish:
 * <ProgressIndicator wsUrl="ws://.../ws/notifications?token=...&task_id=..." />
 */

const RECONNECT_DELAY_MS = 2000;
const MAX_RECONNECT_DELAY_MS = 30000;

export default function ProgressIndicator({ redirectPath = "/watch-video" }) {
  const taskId = localStorage.getItem("taskId");
  const wsUrl = useMemo(() => {
    const token = localStorage.getItem("admin_token");

    // if (!token || !taskId) return null;
    return `wss://videogenerator.soof.uz/ws/notifications?token=${token}&task_id=${taskId}`;
  }, []);

  const [currentPage, setCurrentPage] = useState(null);
  const [phase, setPhase] = useState("connecting"); // connecting | running | completed | closed
  const navigate = useNavigate();

  const wsRef = useRef(null);
  const reconnectAttempt = useRef(0);
  const reconnectTimer = useRef(null);
  const mountedRef = useRef(true);
  const phaseRef = useRef(phase);
  const [cancelBtn, setCancelBtn] = useState(false);
  phaseRef.current = phase;
  useEffect(() => {
    mountedRef.current = true;

    const connect = () => {
      if (!wsUrl) {
        setPhase("closed");
        return;
      }
      setPhase((prev) => (prev === "completed" ? prev : "connecting"));
      let socket;
      try {
        socket = new WebSocket(wsUrl);
      } catch {
        scheduleReconnect();
        return;
      }
      wsRef.current = socket;
      socket.onopen = () => {
        if (!mountedRef.current) return;
        reconnectAttempt.current = 0;
      };
      socket.onmessage = (event) => {
        if (!mountedRef.current) return;
        let data;
        try {
          data = JSON.parse(event.data);
        } catch {
          return;
        }
        if (data.event === "progress") {
          setPhase("running");
          setCancelBtn(true)
          if (typeof data.current_page !== "undefined") {
            setCurrentPage(data.current_page);
          }
        } else if (data.event === "completed") {
          setPhase("completed");
          navigate(redirectPath);
        } else if (data.event === "failed") {
          toast.error("Video yaratishda xatolik yuz berdi");
          navigate("/new-lesson");
        } else if (data.event === "progress") {
          setCancelBtn(true)
        }
      };
      socket.onerror = () => { };
      socket.onclose = () => {
        if (!mountedRef.current) return;
        if (phaseRef.current !== "completed") {
          setPhase("closed");
          scheduleReconnect();
        }
      };
    };

    const scheduleReconnect = () => {
      if (reconnectTimer.current) return;
      const delay = Math.min(
        RECONNECT_DELAY_MS * 2 ** reconnectAttempt.current,
        MAX_RECONNECT_DELAY_MS
      );
      reconnectTimer.current = setTimeout(() => {
        reconnectTimer.current = null;
        reconnectAttempt.current += 1;
        connect();
      }, delay);
    };

    connect();

    return () => {
      mountedRef.current = false;
      if (reconnectTimer.current) clearTimeout(reconnectTimer.current);
      wsRef.current?.close();
    };
  }, [wsUrl]);

  const handleCancelGenerate = () => {
    axios.delete(`${M_URL}/video-generate/cancel/${taskId}`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("admin_token")}`,
      },
    }).then((res) => {
      setCancelBtn(false);
      navigate("/history");
    })
      .catch((err) => console.log(err))
  }


  if (phase === "completed") {
    return (
      <div style={wrapperStyle}>
        <div style={cardStyle}>
          <CheckCircle2 size={22} color="#15875E" strokeWidth={2.2} />
          <span style={labelStyle}>Tayyor, video sahifasiga o'tilmoqda...</span>
        </div>
      </div>
    );
  }

  if (!wsUrl) {
    return (
      <div style={wrapperStyle}>
        <div style={cardStyle}>
          <span style={labelStyle}>Sessiya topilmadi, qaytadan kiring</span>
        </div>
      </div>
    );
  }

  if (phase === "connecting" || currentPage === null) {
    return (
      <div style={wrapperStyle}>
        <div style={cardStyle}>
          <Loader2 size={20} color="#2C6FCC" strokeWidth={2.4} style={spinStyle} />
          <span style={labelStyle}>Ulanmoqda...</span>
        </div>
        <style>{`@keyframes piSpin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return (
    <div style={wrapperStyle}>
      <div style={cardStyle}>
        <Loader2 size={20} color="#2C6FCC" strokeWidth={2.4} style={spinStyle} />
        <span style={labelStyle}>
          {currentPage}-sahifa ishlanmoqda
        </span>
      </div>
      <style>{`@keyframes piSpin { to { transform: rotate(360deg); } }`}</style>
      {cancelBtn && <button onClick={handleCancelGenerate} style={cancelBtnStyle} >Cancel</button>}
    </div>
  );
}

const wrapperStyle = {
  width: "100%",
  height: "100vh",
  display: "flex",
  justifyContent: "center",
  flexDirection: "column",
  alignItems: "center",
  fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Inter, sans-serif',
};

const cancelBtnStyle = {
  display: "inline-block",
  alignItems: "center",
  gap: "6px",
  height: "32px",
  padding: "0 12px",
  fontSize: "13px",
  fontWeight: 500,
  color: "#fff",
  background: "#e24b4a",
  border: "none",
  borderRadius: "6px",
  cursor: "pointer",
  marginTop: "30px",
}

const cardStyle = {
  display: "flex",
  alignItems: "center",
  gap: 10,
  padding: "10px 18px",
  borderRadius: 999,
  background: "#FFFFFF",
  border: "1px solid #E4E6EA",
  boxShadow: "0 1px 2px rgba(20,22,28,0.04)",
};

const labelStyle = {
  fontSize: 24,
  fontWeight: 600,
  color: "#1A1C20",
  whiteSpace: "nowrap",
};

const spinStyle = {
  animation: "piSpin 0.85s linear infinite",
  flexShrink: 0,
};